import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 120px;
  background: #fff;
  border-radius: 15px;
  font-family: 'san-serif';
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 350px;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 50px;
`;



const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 32px;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  min-width: 80px;
  color: #222;
  margin-left: 22px;
`;

const SigValue = styled.span`
  min-width: 100px;
  border-bottom: 1px solid #333;
  padding: 0 4px;
  font-weight: 500;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const StudentIdForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container ref={docRef}>
      <PrintHideStyle />
      <Logo src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" />
      <div style={{textAlign: 'center', marginBottom: 8, fontWeight: 600, fontSize: '1.1rem'}}>Chicago Community Learning Center</div>
      <div style={{textAlign: 'center', fontSize: '0.98rem', marginBottom: 2}}>
        Chicago Campus: 840 W. Irving Park Road, suite 203, Illinois 60613 Office: (773) 506-1503<br/>
        Evergreen Campus: 9730 S. Western Ave, suite 502, Evergreen Park Illinois 60805
      </div>
      <div style={{textAlign: 'center', fontSize: '0.98rem', marginBottom: 12}}>
        <a href="https://www.cclctraining.org" target="_blank" rel="noopener noreferrer">www.cclctraining.org</a>
      </div>
      <Title ><u>Student ID Form</u></Title>
      <div style={{fontSize: '1.1rem', margin: '32px 0 32px 0', textAlign: 'left'}}>
        My student ID is my responsibility to have on and visible during class and during clinical.
      </div>
      <div style={{fontSize: '1.1rem', margin: '40px 0 24px 0', textAlign: 'left'}}>
        <span style={{fontSize: '1.3rem', color: '#334293', marginRight: 12}}>✓</span>
        If lost there will be a $5.00 fee for a replacement ID.
      </div>
      <div style={{fontSize: '1.1rem', margin: '24px 0 40px 0', textAlign: 'left'}}>
        <span style={{fontSize: '1.3rem', color: '#334293', marginRight: 12}}>✓</span>
        I have read, agree and will be responsible to the terms of having a student ID.
      </div>
      <SignatureRow style={{marginTop: 102}}>
        <SigLabel>Printed Name:</SigLabel>
        <SigValue style={{minWidth: 160}}>{student.firstName} {student.lastName}</SigValue>
        <SigLabel>Date:</SigLabel>
        <SigValue>{dateStr}</SigValue>
      </SignatureRow>
      <SignatureRow>
        <SigLabel>Signature:</SigLabel>
        <SigValue style={{minWidth: 160}}>
          {student.student_ack_signature_data ? (
            <div>
              {(() => {
                const sigData = student.student_ack_signature_data;
                
                // Check if it's a valid data URL (must have content after data:)
                if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                  return (
                    <img 
                      src={sigData} 
                      alt="Student Signature" 
                      style={{ 
                        maxHeight: '40px', 
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  );
                }
                
                // Check if it's SVG markup
                if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: sigData }}
                      style={{ maxHeight: '40px' }}
                    />
                  );
                }
                
                // Check if it's a base64 string (without data URL prefix)
                if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                  return (
                    <img 
                      src={`data:image/png;base64,${sigData}`} 
                      alt="Student Signature" 
                      style={{ 
                        maxHeight: '40px', 
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }} 
                    />
                  );
                }
                
                // Check if it's a file path
                if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                  return (
                    <img 
                      src={sigData} 
                      alt="Student Signature" 
                      style={{ 
                        maxHeight: '40px', 
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  );
                }
                
                // Check for incomplete data URL - return empty for incomplete data
                if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length <= 6) {
                  return null;
                }
                
                return (
                  <div style={{ fontSize: '0.8rem', color: '#666', wordBreak: 'break-all' }}>
                    <div>Format: {typeof sigData}</div>
                    <div>Length: {sigData.length}</div>
                    <div>Preview: {sigData.substring(0, 50)}...</div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No signature</span>
          )}
        </SigValue>
        <SigLabel>Date:</SigLabel>
        <SigValue>{dateStr}</SigValue>
      </SignatureRow>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default StudentIdForm; 